/**
 * For the most part this is a copy of the previous Digital Id Verification presentational component.
 * This is still a big mess and can be improved...
 * FIXME: @krochet94 or @iamnaj1
 */

import { User } from '@firebase/auth';
import { styled } from '@mui/material/styles';
import { captureException } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { Timestamp, doc, getDoc } from '@firebase/firestore';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';

import type { UserType } from '@shared/auth/index.js';
import type { CurrentStatus, IntakeStatusBaseSteps, StatusActivityStatus } from '@shared/patient-intake-status';

import { db } from '../../../../firebase-config.js';
import { useAuth } from '../../../../hooks/index.js';
import checkAppToken from '../../../../functions/check-app-token.js';
import { parseName } from '../utils';

declare global {
  interface Window {
    digitalId?: {
      init: (config: { clientId: string; uxMode: string; onComplete: (response: { code: string }) => void }) => void;
    };
  }
}

const DIGITAL_ID_SCRIPT_ID = 'digitalid-script';
const DIGITAL_ID_SCRIPT_URL = `https://${import.meta.env.VITE_APP_DIGITAL_ID_HOST}.com/sdk/app.js`;
const DIGITAL_ID_VERIFY_API_URL = `https://${import.meta.env.VITE_APP_CLOUD_FUNCTION_LOCATION}.cloudfunctions.net/verifyIdentity`;

type DigitalIdCustomerData = {
  given_name: string;
  family_name: string;
  email: string;
  phone: string;
};

const PREFIX = 'DigitalIdVerification';

const classes = {
  box: `${PREFIX}-box`,
  icon: `${PREFIX}-icon`,
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.box}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
  },

  [`& .${classes.icon}`]: {
    fontSize: 70,
    color: theme.palette.primary.main,
  },

  [`& .${classes.container}`]: {
    margin: theme.spacing(3),
  },
}));

type DigitalIdVerificationProps = {
  onSuccess?: () => void;
  onClose?: () => void;
  noTitle?: boolean;
  isModal?: boolean;
  description?: React.ReactNode;
} & (
  | {
      patientId: string | undefined;
    }
  | {
      patientInfo: Record<string, unknown>;
    }
);

const getPatientData = async ({ props, user }: { props: DigitalIdVerificationProps; user: User | null }) => {
  if ('patientInfo' in props) {
    return { data: props.patientInfo, id: props.patientInfo.id as string };
  }

  const targetPatientId = props.patientId || user?.uid;

  if (!targetPatientId) {
    return { data: null, id: null };
  }

  const patientDoc = await getDoc(doc(db, 'patients', targetPatientId));

  return { data: patientDoc.data(), id: targetPatientId };
};

const getIdVerificationIntakeStatus = async (targetPatientId: string) => {
  const patientDoc = await getDoc(doc(db, 'patients', targetPatientId, 'intakeStatus', 'ID_VERIFICATION'));

  return { data: patientDoc.data() as IntakeStatusBaseSteps['ID_VERIFICATION'] | undefined };
};

type SetupDigitalIdRequestParams = {
  verificationStatus: StatusActivityStatus;
  user: User | null;
  props: DigitalIdVerificationProps;
  setCustomerData: React.Dispatch<React.SetStateAction<DigitalIdCustomerData | null>>;
  setIsDifferentLegalName: React.Dispatch<React.SetStateAction<boolean>>;
};

const setupDigitalIdRequest = ({
  verificationStatus,
  user,
  props,
  setCustomerData,
  setIsDifferentLegalName,
}: SetupDigitalIdRequestParams) => {
  // Don't initialise if already verified
  if (verificationStatus === 'COMPLETED') {
    return () => {};
  }

  const script = document.createElement('script');
  script.src = DIGITAL_ID_SCRIPT_URL;
  script.async = true;
  script.id = DIGITAL_ID_SCRIPT_ID;

  document.body.appendChild(script);

  const verifyIdentityAPI = DIGITAL_ID_VERIFY_API_URL;

  script.onload = async () => {
    const token = await checkAppToken();

    if (!token) {
      throw new Error('App Check token not found');
    }

    if (!window?.digitalId || !import.meta.env.VITE_APP_DIGITAL_ID_PUBLIC_KEY) {
      throw new Error('Digital ID SDK not loaded');
    }

    const { data: patientData, id: targetPatientId } = await getPatientData({ props, user });

    if (!patientData || !targetPatientId) {
      throw new Error('Patient ID not found');
    }

    window.digitalId.init({
      clientId: import.meta.env.VITE_APP_DIGITAL_ID_PUBLIC_KEY,
      uxMode: 'popup',
      onComplete: async (response) => {
        try {
          const headers = new Headers();
          headers.append('Content-Type', 'application/json');
          headers.append('X-Firebase-AppCheck', token);

          const res = await fetch(verifyIdentityAPI, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              code: response.code,
              user,
            }),
          });

          const data = await res.json();

          if (!data) {
            throw new Error('An error occurred while verifying your identity');
          }

          const { decodedData, error } = data;

          if (error || !decodedData) {
            throw new Error(`An error occurred while verifying your identity: ${error?.message}`);
          }

          const isDifferentLegalName =
            patientData?.legalFirstName?.toUpperCase() !== patientData?.firstName?.toUpperCase() ||
            patientData?.legalLastName?.toUpperCase() !== patientData?.lastName?.toUpperCase();

          setCustomerData(decodedData);
          setIsDifferentLegalName(isDifferentLegalName);
        } catch (err) {
          captureException(err, {
            tags: { issueIn: 'DigitalIdVerification.setupDigitalIdRequest' },
            extra: {
              user,
              props,
              verificationStatus,
            },
          });

          throw err;
        }
      },
    });
  };

  return () => {
    const scriptElement = document.getElementById('digitalid-script');

    if (scriptElement && scriptElement.parentNode) {
      scriptElement.parentNode.removeChild(scriptElement);
    }
  };
};

type ContentProps = {
  isLoading: boolean;
  verificationStatus: StatusActivityStatus;
  verifiedByName: string | null;
  verifiedAt: Timestamp | null;
  error: string | null;
  customerData: DigitalIdCustomerData | null;
  isDifferentLegalName: boolean;
  description?: React.ReactNode;
  noTitle?: boolean;
  handleSuccess: () => void;
};

const Content: React.FC<ContentProps> = ({
  isLoading,
  verificationStatus,
  verifiedByName,
  verifiedAt,
  error,
  customerData,
  isDifferentLegalName,
  noTitle = false,
  description,
  handleSuccess,
}) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (verificationStatus === 'COMPLETED') {
    return (
      <Box className={classes.container}>
        <HowToRegIcon className={classes.icon} color="success" />
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          ID Verification Complete
        </Typography>

        {verifiedByName && (
          <Typography variant="body1" gutterBottom>
            Verified by {verifiedByName}
          </Typography>
        )}

        {verifiedAt && (
          <Typography variant="body2" gutterBottom>
            on {verifiedAt.toDate().toLocaleString()}
          </Typography>
        )}
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  if (!customerData) {
    return (
      <Box className={classes.container}>
        {!noTitle && (
          <Typography variant="h5" gutterBottom>
            Verify your Identity
          </Typography>
        )}

        {description}

        <div id="digitalid-verify" />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <HowToRegIcon className={classes.icon} />
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Verification Successful
      </Typography>

      <Typography variant="h4" gutterBottom>
        {customerData.given_name} {customerData.family_name}
      </Typography>

      {isDifferentLegalName && (
        <Typography variant="body1" color="warning.main" gutterBottom>
          Verified name is not the same as the given name. We will only use your verified name on prescriptions and any
          other legal documents.
        </Typography>
      )}

      <Button variant="contained" color="primary" onClick={handleSuccess} sx={{ mt: 2 }} loading={isLoading}>
        Proceed
      </Button>
    </Box>
  );
};

export const DigitalIdVerification: React.FC<DigitalIdVerificationProps> = ({
  onSuccess,
  onClose,
  description,
  isModal = false,
  noTitle = false,
  ...props
}) => {
  const { user, userType } = useAuth();
  const [customerData, setCustomerData] = useState<DigitalIdCustomerData | null>(null);
  const [isDifferentLegalName, setIsDifferentLegalName] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<StatusActivityStatus>('NOT_STARTED');
  const [verifiedByName, setVerifiedByName] = useState<string | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<Timestamp | null>(null);

  // Check if verification is already completed
  const checkVerificationStatus = React.useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: patientData, id: targetPatientId } = await getPatientData({ props, user });

      if (!patientData) {
        setIsLoading(false);

        return;
      }

      const { data: intakeStatus } = await getIdVerificationIntakeStatus(targetPatientId);

      const lastActivity =
        intakeStatus?.currentStatus ??
        ({
          status: patientData.identification === 'verified' ? 'COMPLETED' : 'NOT_STARTED',
          actionedAt: null,
          note: null,
          metadata: null,
          actionedBy: {
            id: targetPatientId,
            name: parseName(patientData.firstName, patientData.lastName),
            email: patientData?.email || 'no email',
            userType: userType as UserType,
          },
        } satisfies CurrentStatus<null>);

      setVerificationStatus(lastActivity.status);

      setVerifiedByName(() => {
        if (lastActivity.status !== 'COMPLETED') {
          return null;
        }

        if (lastActivity.actionedBy?.id === user?.uid) {
          return 'You';
        }

        return lastActivity.actionedBy?.name ?? null;
      });
      setVerifiedAt(lastActivity.actionedAt ?? null);

      setIsLoading(false);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error checking verification status:', err);
      }

      captureException(err, {
        tags: { issueIn: 'DigitalIdVerification.checkVerificationStatus' },
        extra: {
          user,
          props,
          verificationStatus,
        },
      });
      setIsLoading(false);
    }
  }, [user?.uid, userType]);

  const handleDigitalIdRequest = React.useCallback(() => {
    return setupDigitalIdRequest({ verificationStatus, user, props, setCustomerData, setIsDifferentLegalName });
  }, [user, verificationStatus]);

  const handleSuccess = async () => {
    try {
      onSuccess?.();
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Error updating verification status:', err);
      }

      captureException(err, {
        tags: { issueIn: 'DigitalIdVerification.handleSuccess' },
        extra: {
          user,
          props,
          verificationStatus,
        },
      });
      setError('Failed to update verification status');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // First check if verification is already completed
    checkVerificationStatus();
  }, [checkVerificationStatus]);

  useEffect(() => {
    // Only initialise Digital ID after checking verification status
    if (!isLoading) {
      const unregister = handleDigitalIdRequest();
      return () => unregister();
    }
  }, [handleDigitalIdRequest, isLoading]);

  if (isModal) {
    return (
      <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
        <DialogTitle>ID Verification</DialogTitle>
        <DialogContent>
          <StyledBox className={classes.box}>
            <Stack spacing={3} width="100%">
              <Content
                isLoading={isLoading}
                verificationStatus={verificationStatus}
                verifiedByName={verifiedByName}
                verifiedAt={verifiedAt}
                error={error}
                customerData={customerData}
                isDifferentLegalName={isDifferentLegalName}
                handleSuccess={handleSuccess}
              />
            </Stack>
          </StyledBox>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <StyledBox className={classes.box}>
      <Stack spacing={3} width="100%">
        <Content
          isLoading={isLoading}
          verificationStatus={verificationStatus}
          verifiedByName={verifiedByName}
          verifiedAt={verifiedAt}
          error={error}
          customerData={customerData}
          isDifferentLegalName={isDifferentLegalName}
          handleSuccess={handleSuccess}
          noTitle={noTitle}
          description={description}
        />
      </Stack>
    </StyledBox>
  );
};
